/**
 * Recruiting API fetch wrapper — ported from Operations `api-client.ts`.
 *
 * - Attaches `Authorization: Bearer <token>` + `X-Innu-App: recruiting` on
 *   every call, plus `X-Selected-Company-Id` when the Shell handed us one.
 * - Unwraps the worker's envelope: `{ ok: true, data }` resolves to `data`;
 *   `{ ok: false, error: { code, message } }` or any non-2xx throws `ApiError`
 *   carrying `status`, `code` and the parsed body.
 * - On 401: calls `onUnauthorized()` to obtain a fresh token, retries once.
 *   If still 401, throws — the caller can redirect to the Innu Shell.
 * - The token is read lazily (function, not value) so a refresh in flight is
 *   picked up by the next request without re-creating the client.
 */
import { useMemo } from 'react';
import { getStoredSelectedCompanyId, useAuth } from './auth';

const DEFAULT_BASE_URL = import.meta.env.VITE_RECRUITING_API_BASE ?? 'http://localhost:8787';
/** Worker origin for callers that must bypass the JSON wrapper (multipart upload, blob download). */
export const API_BASE_URL: string = DEFAULT_BASE_URL;

export class ApiError extends Error {
  status: number;
  code: string;
  body: unknown;
  constructor(status: number, body: unknown, message: string, code = 'http_error') {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

export interface ApiErrorBody {
  code: string;
  message: string;
}

export type Envelope<T> =
  | { ok: true; data: T }
  | { ok: false; error: ApiErrorBody };

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  baseUrl?: string;
}

export interface TokenProvider {
  /** Always-current access token (read at request time). */
  getToken(): string | null;
  /** Called on 401. Returns a new access token, or null to give up. */
  onUnauthorized?(): Promise<string | null>;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isEnvelope(v: unknown): v is Envelope<unknown> {
  return isRecord(v) && typeof v.ok === 'boolean';
}

function errorBodyOf(v: unknown): ApiErrorBody | null {
  if (!isRecord(v) || !isRecord(v.error)) return null;
  const { code, message } = v.error;
  return {
    code: typeof code === 'string' ? code : 'unknown',
    message: typeof message === 'string' ? message : 'Request failed',
  };
}

async function doFetch(
  token: string | null,
  path: string,
  opts: RequestOptions,
): Promise<Response> {
  const { baseUrl = DEFAULT_BASE_URL, body, headers, ...rest } = opts;

  const finalHeaders = new Headers(headers);
  finalHeaders.set('X-Innu-App', 'recruiting');
  finalHeaders.set('Accept', 'application/json');
  if (token) finalHeaders.set('Authorization', `Bearer ${token}`);
  const companyId = getStoredSelectedCompanyId();
  if (companyId && !finalHeaders.has('X-Selected-Company-Id')) {
    finalHeaders.set('X-Selected-Company-Id', companyId);
  }
  if (body !== undefined && !finalHeaders.has('Content-Type')) {
    finalHeaders.set('Content-Type', 'application/json');
  }

  const url = path.startsWith('http') ? path : `${baseUrl}${path}`;
  return fetch(url, {
    ...rest,
    headers: finalHeaders,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

async function parseResponse<T>(res: Response, path: string): Promise<T> {
  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  const err = errorBodyOf(parsed);
  if (!res.ok) {
    throw new ApiError(
      res.status,
      parsed,
      err?.message ?? `API ${res.status} ${res.statusText} — ${path}`,
      err?.code,
    );
  }

  if (isEnvelope(parsed)) {
    if (parsed.ok) {
      // `/health` answers `{ ok: true, service }` with no `data`; pass it through.
      return ('data' in parsed ? parsed.data : parsed) as T;
    }
    throw new ApiError(res.status, parsed, err?.message ?? `API error — ${path}`, err?.code);
  }
  return parsed as T;
}

async function rawRequest<T>(
  provider: TokenProvider,
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  let res = await doFetch(provider.getToken(), path, opts);
  if (res.status === 401 && provider.onUnauthorized) {
    const newToken = await provider.onUnauthorized();
    if (newToken) {
      res = await doFetch(newToken, path, opts);
    }
  }
  return parseResponse<T>(res, path);
}

export interface ApiClient {
  get<T>(path: string, opts?: RequestOptions): Promise<T>;
  post<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T>;
  patch<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T>;
  delete<T>(path: string, opts?: RequestOptions): Promise<T>;
}

/**
 * Accepts either a static token (used in tests) or a TokenProvider (used in
 * app code via useApiClient).
 */
export function createApiClient(input: TokenProvider | string | null): ApiClient {
  const provider: TokenProvider =
    typeof input === 'object' && input !== null && 'getToken' in input
      ? input
      : { getToken: () => (typeof input === 'string' ? input : null) };

  // The verb is a DEFAULT, not a mandate: `...opts` after it lets callers
  // override the method when a route needs PUT.
  return {
    get: (path, opts) => rawRequest(provider, path, { method: 'GET', ...opts }),
    post: (path, body, opts) => rawRequest(provider, path, { method: 'POST', ...opts, body }),
    patch: (path, body, opts) => rawRequest(provider, path, { method: 'PATCH', ...opts, body }),
    delete: (path, opts) => rawRequest(provider, path, { method: 'DELETE', ...opts }),
  };
}

/**
 * React hook — bound to the active auth context. Use inside components.
 * On 401, calls `auth.refresh()` and retries once before throwing.
 */
export function useApiClient(): ApiClient {
  const auth = useAuth();
  return useMemo(
    () =>
      createApiClient({
        getToken: auth.getToken,
        onUnauthorized: auth.refresh,
      }),
    [auth.getToken, auth.refresh],
  );
}
